import type { AnnotationsConfigProperties } from '../types/annotations-config-properties.js';

/**
 * Strips render-prop/function fields (`tooltip.template`, per-track and chart-level
 * `annotationsActions`) from a resolved internal annotations configuration, producing the
 * JSON-serializable shape returned by `getConfig()` and accepted back through the
 * `HistogramChartConfig`/`TimeseriesChartConfig` providers.
 *
 * Also strips any non-string `symbol` from markers — React elements are not JSON-serializable
 * and must not appear in the exported payload.
 */
export function getExportableAnnotationsConfig<
  TTrack extends {
    markers: Array<{ symbol?: unknown } & Record<string, unknown>>;
    annotationsActions?: unknown;
  },
  TAnnotations extends {
    tracks: TTrack[];
    tooltip?: { hidden?: boolean; template?: unknown };
    annotationsActions?: unknown;
  },
>(
  annotations: TAnnotations | undefined,
): AnnotationsConfigProperties | undefined {
  if (!annotations) {
    return undefined;
  }

  const {
    tooltip,
    annotationsActions: _chartActions,
    tracks,
    ...rest
  } = annotations;

  return {
    ...rest,
    tracks: tracks.map(
      ({ annotationsActions: _trackActions, markers, ...track }) => ({
        ...track,
        markers: markers.map(({ symbol, ...marker }) => ({
          ...marker,
          ...(typeof symbol === 'string' ? { symbol } : {}),
        })),
      }),
    ),
    tooltip: { hidden: !!tooltip?.hidden },
  } as unknown as AnnotationsConfigProperties;
}
